import { Box, Divider, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import useColors from "../../../../../../../api/hooks/useColors";
import { filterName } from "../../../../filters/utils";

const OsCustomBarChart = ({
  dataset,
  maxCount,
  appliedFilters,
  setAppliedFilters,
  fullOSToName,
  latestToggleOn,
}) => {
  const colors = useColors();
  const renderTooltip = (value, event) => {
    const barIndex = value.itemData.dataIndex;
    const itemData = dataset[barIndex];
    const key = !itemData?.olderVersion
      ? itemData?.os.replace(" (latest)", "")
      : itemData?.os;

    const osWithoutTheLastPoint = fullOSToName[key]?.map((version) => {
      // Add .0 to the version if it has less than 2 dots
      while ((version.match(/\./g) || []).length < 2) {
        version += ".0";
      }
      // Remove the number third point (for windows)
      return version.replace(/^(\d+\.\d+\.\d+)(\.\d+)?$/, "$1");
    });

    const osVersions = Array.from(new Set(osWithoutTheLastPoint));
    const count = value.series.data[barIndex];
    if (count < 1) return null;
    if (latestToggleOn && !itemData?.olderVersion) {
      return (
        <Box
          sx={{
            padding: "10px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row",
            top: "-15px",
            left: "40px",
            backgroundColor: colors.common_white,
            boxShadow: "0px 1px 2px " + colors.black_24percent,
            "&::before": {
              content: '""',
              position: "absolute",
              width: 0,
              height: 0,
              borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderRight: `6px solid ${colors.grey_200}`,
              top: "calc(50% - 6px)",
              left: "-6px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <Typography variant="overline"> {key}</Typography>
            <Divider orientation="vertical" flexItem />
            {osVersions?.map((version, index) => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
                key={version}
              >
                <Typography key={version} variant="caption">
                  {version}
                </Typography>
                {index !== osVersions.length - 1 && (
                  <Divider orientation="vertical" flexItem />
                )}
              </Box>
            ))}{" "}
          </Box>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            height: "22px",
            padding: "10px",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            top: "-30px",
            left: "-30px",
            backgroundColor: colors.common_white,
            boxShadow: "0px 1px 2px " + colors.black_24percent,
            "&::after": {
              content: '""',
              position: "absolute",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `6px solid ${colors.common_white}`,
              top: "100%",
              left: "calc(50% - 6px)",
            },
          }}
        >
          <Typography variant="caption"> {count}</Typography>
        </Box>
      );
    }
  };

  const CustomBarLabel = (props) => {
    const height = props.style.height.animation.to + "px";
    const width = props.style.width.animation.to + "px";
    const x =
      props.style.x.animation.to +
      parseInt(props.style.width.animation.to) / 2 +
      2 +
      "px"; // 2px is the padding
    const y = props.style.y.animation.to + 4.5 + "px"; // 4.5px is the padding
    return (
      <text
        style={{
          height: height,
          width: width,
          font: "13px sans-serif",
          fill: colors.text_secondary,
          transform: `translate3d(${x},${y}, 0px)`,
        }}
      >
        {props.children}
      </text>
    );
  };
  // filter out the latest version of the OS
  const onBarClick = (event, clickedBarData) => {
    const osIndex = clickedBarData.dataIndex;
    const osData = dataset[osIndex];
    const key = !osData?.olderVersion
      ? osData?.os.replace(" (latest)", "").replace(" (Supported)", "")
      : osData?.os;
    const newFilters = fullOSToName[key]?.reduce(
      (acc, version) => ({ ...acc, [version]: true }),
      {}
    );
    setAppliedFilters({
      ...appliedFilters,

      [filterName.HOST_OS_FULL_VERSION]: {
        ...appliedFilters[filterName.HOST_OS_FULL_VERSION],
        ...newFilters,
      },
    });
  };

  return (
    <BarChart
      onItemClick={onBarClick}
      sx={{
        zIndex: 2,
        "& .MuiChartsAxis-root.MuiChartsAxis-directionX.MuiChartsAxis-bottom ":
          {
            display: "none",
          },
        "& .MuiChartsAxis-tick": {
          strokeWidth: 0,
        },
        "& .MuiChartsAxis-left .MuiChartsAxis-line": {
          strokeWidth: 0,
        },
        "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
          strokeWidth: 0,
        },

        "& .MuiChartsAxisHighlight-root": {
          fill: "none",
        }, // color both axis labels
        "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
          fill: colors.text_secondary,
        },
      }}
      dataset={dataset}
      barLabel={(item, context) => {
        if (item.value < 1) return "0";
        return item.value;
      }}
      slots={{
        barLabel: CustomBarLabel,
      }}
      yAxis={[
        {
          scaleType: "band",
          dataKey: "os",
          colorMap: {
            type: "ordinal",
            colors: dataset.map((item) => item.color || "#000000"),
          },
        },
      ]}
      xAxis={[
        {
          max: maxCount,
        },
      ]}
      tooltip={{
        trigger: "item",
        itemContent: renderTooltip,
      }}
      series={[{ dataKey: "count" }]}
      layout="horizontal"
      height={120}
      margin={{
        top: 0,
        left: 180,
      }}
      borderRadius={5}
    />
  );
};

export default OsCustomBarChart;
